export type RgbColor = {
  r: number;
  g: number;
  b: number;
};

export const parseColorToRgb = (color: string): RgbColor | null => {
  const normalized = color.trim();
  if (normalized.startsWith('#')) {
    let hex = normalized.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char) => char + char)
        .join('');
    }
    if (/^[0-9a-f]{6}$/i.test(hex)) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
  }

  const rgbMatch = normalized.match(/rgba?\(([^)]+)\)/i);
  if (rgbMatch) {
    const parts = rgbMatch[1]
      .split(',')
      .map((part) => part.trim())
      .slice(0, 3)
      .map((value) => Number(value));
    if (parts.every((value) => !Number.isNaN(value))) {
      const [r, g, b] = parts;
      return { r, g, b };
    }
  }
  return null;
};

export const rgbToHsl = (r: number, g: number, b: number) => {
  const normalizedR = r / 255;
  const normalizedG = g / 255;
  const normalizedB = b / 255;
  const max = Math.max(normalizedR, normalizedG, normalizedB);
  const min = Math.min(normalizedR, normalizedG, normalizedB);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const delta = max - min;
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    switch (max) {
      case normalizedR:
        h =
          (normalizedG - normalizedB) / delta +
          (normalizedG < normalizedB ? 6 : 0);
        break;
      case normalizedG:
        h = (normalizedB - normalizedR) / delta + 2;
        break;
      case normalizedB:
        h = (normalizedR - normalizedG) / delta + 4;
        break;
    }
    h /= 6;
  }
  return { h, s, l };
};

const hueToRgb = (p: number, q: number, t: number) => {
  let tempT = t;
  if (tempT < 0) tempT += 1;
  if (tempT > 1) tempT -= 1;
  if (tempT < 1 / 6) return p + (q - p) * 6 * tempT;
  if (tempT < 1 / 2) return q;
  if (tempT < 2 / 3) return p + (q - p) * (2 / 3 - tempT) * 6;
  return p;
};

export const hslToRgb = (h: number, s: number, l: number) => {
  if (s === 0) {
    const value = Math.round(l * 255);
    return { r: value, g: value, b: value };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: Math.round(hueToRgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hueToRgb(p, q, h) * 255),
    b: Math.round(hueToRgb(p, q, h - 1 / 3) * 255),
  };
};

export const adjustColorSaturation = (
  color: string,
  factor: number,
  opacity?: number
) => {
  const rgb = parseColorToRgb(color);
  if (!rgb) return color;
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const newS = Math.max(0, Math.min(1, s * factor));
  const adjusted = hslToRgb(h, newS, l);
  const baseColor = `rgb(${adjusted.r}, ${adjusted.g}, ${adjusted.b})`;
  if (typeof opacity === 'number') {
    const clamped = Math.max(0, Math.min(1, opacity));
    return `rgba(${adjusted.r}, ${adjusted.g}, ${adjusted.b}, ${clamped})`;
  }
  return baseColor;
};
